import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { GraduationCap } from 'lucide-react';

const faqs = [
  {
    question: "Est-ce que Speak&Move est une arnaque ?",
    answer: "Non, Speak&Move n'est pas une arnaque. Nous sommes une structure transparente, immatriculée en France avec une filiale au Cameroun, et nous accompagnons des étudiants d'Afrique francophone dans leur projet d'études en Allemagne avec une méthode claire, des étapes définies et un suivi réel. Nous ne promettons rien que nous ne pouvons pas prouver. Chaque dossier est traité avec sérieux, et nous proposons toujours un échange direct (appel, visio ou rencontre) pour présenter les résultats, répondre aux questions et clarifier chaque étape du parcours. Notre engagement est simple : un accompagnement professionnel, transparent et orienté réussite."
  },
  {
    question: "Combien ça coûte au total ?",
    answer: "Formation complète (A1 à C1) : à partir de 500 000 FCFA. Montage du dossier visa : à partir de 150 000 FCFA. Paiement flexible : possibilité de payer en 3 à 6 fois, sans frais. Après votre test de niveau gratuit, nous vous remettons un devis précis et personnalisé, sans aucun engagement."
  },
  {
    question: "Que se passe-t-il si mon visa est refusé ?",
    answer: "En cas de refus de visa, nous vous accompagnons pour comprendre la décision et préparer une nouvelle demande si vous le souhaitez. Nos frais couvrent le travail réalisé sur votre dossier, mais nous restons à vos côtés pour analyser les motifs du refus et vous proposer la meilleure suite possible."
  },
  {
    question: "Est-ce que je peux travailler en Allemagne avec un visa étudiant ?",
    answer: "Oui, vous pouvez travailler en Allemagne avec un visa étudiant. La loi allemande autorise les étudiants internationaux à travailler jusqu'à 120 jours à temps plein par an, ou 240 jours à temps partiel. C'est idéal pour financer une partie de vos dépenses ou acquérir une première expérience professionnelle. Nous vous expliquons ces règles en détail lors de votre accompagnement, et nous vous aidons aussi à trouver un job étudiant adapté à votre emploi du temps."
  },
  {
    question: "Je n'ai aucune base en allemand, c'est possible ?",
    answer: "Oui, c'est tout à fait possible d'y arriver, même sans aucune base en allemand. La majorité de nos étudiants commencent au niveau zéro, et progressent rapidement grâce à une formation intensive, un suivi régulier et une méthode adaptée aux débutants. Nous vous accompagnons pas à pas : apprentissage de l'allemand du niveau A1 à B2/C1, préparation aux examens officiels, soutien constant pour garder le rythme, conseils personnalisés selon votre progression. Avec de la régularité et un bon encadrement, vous pouvez atteindre le niveau requis pour étudier ou travailler en Allemagne."
  },
  {
    question: "Pourquoi l'Allemagne et pas la France ou le Canada ?",
    answer: "L'Allemagne offre aujourd'hui l'un des meilleurs rapports qualité-opportunités au monde pour les étudiants internationaux. Contrairement à d'autres destinations comme la France ou le Canada, l'Allemagne combine : des études quasiment gratuites (même pour les étrangers), un marché du travail en forte demande (notamment dans les secteurs techniques, médicaux et industriels), un visa étudiant flexible qui permet de travailler pendant les études, un taux d'employabilité très élevé après le diplôme, et une voie claire vers la résidence permanente pour ceux qui souhaitent rester. La France et le Canada sont de bonnes destinations, mais l'Allemagne se distingue par la stabilité de son système, la qualité de ses universités publiques et la facilité d'intégration professionnelle après les études. C'est pour cela que de plus en plus d'étudiants africains choisissent l'Allemagne : moins de frais, plus d'opportunités, et un avenir professionnel solide."
  },
  {
    question: "Comment se déroule l'alternance ('Ausbildung') avec vos partenaires ?",
    answer: "Nos partenaires en Allemagne proposent des programmes de formation en alternance ('Ausbildung'), reconnus par l'État allemand. Ces programmes permettent aux étudiants de se former tout en travaillant, avec un salaire mensuel et une forte probabilité d'embauche à la fin. Speak&Move s'occupe de : vous préparer au niveau d'allemand requis, constituer votre dossier, vous positionner auprès de nos partenaires, vous accompagner jusqu'à votre arrivée et votre intégration. Nos partenaires, eux, vous accueillent dans leur structure et vous offrent une formation + un contrat de travail dans le cadre du système allemand qui vise à combler le manque de main-d'œuvre qualifiée. En résumé : Speak&Move vous prépare et vous accompagne, nos partenaires vous forment et vous emploient."
  }
];

const FAQSection = () => {
  return (
    <section id="faq" className="py-16 md:py-24 bg-gray-50" data-testid="faq-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left - Image (Desktop) */}
          <div className="relative hidden lg:flex items-center justify-center">
            <div className="sticky top-32 w-full flex justify-center">
              {/* Image Container */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_515c4a64-eec4-4be1-ae71-705e6633de80/artifacts/mylrthxt_medium-shot-friends-outdoors.jpg"
                  alt="Étudiants Speak&Move heureux"
                  className="w-full h-auto object-cover aspect-square"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                
                {/* Floating testimonial badge */}
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <GraduationCap className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">+45 étudiants accompagnés</p>
                      <p className="text-sm text-gray-500">Rejoignez notre communauté</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/20 rounded-full blur-2xl -z-10" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-secondary/20 rounded-full blur-2xl -z-10" />
            </div>
          </div>
          
          {/* Right - FAQ Content */}
          <div>
            {/* Section Header */}
            <div className="text-center lg:text-left mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                FAQ
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Questions fréquentes ?
              </h2>
            </div>

            {/* FAQ Accordion */}
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white rounded-xl border-0 shadow-sm px-6 data-[state=open]:shadow-md transition-shadow"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left hover:no-underline py-5">
                    <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-5 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            
            {/* CTA */}
            <div className="text-center lg:text-left mt-8">
              <p className="text-gray-600 mb-4">Vous n'avez pas trouvé la réponse à votre question ?</p>
              <a
                href="https://wa.me/33761432040?text=Bonjour%20Speak%26Move%2C%20j'ai%20une%20question..."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary font-semibold hover:underline"
                data-testid="faq-whatsapp-link"
              >
                💬 Posez-la nous sur WhatsApp
              </a>
            </div>
            
            {/* Mobile Image */}
            <div className="lg:hidden mt-12">
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_515c4a64-eec4-4be1-ae71-705e6633de80/artifacts/mylrthxt_medium-shot-friends-outdoors.jpg"
                  alt="Étudiants Speak&Move heureux"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <p className="font-semibold">+500 étudiants accompagnés</p>
                  <p className="text-sm text-white/80">Rejoignez notre communauté</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
